import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import AdminLayout from "../../components/AdminLayout";
import {
  fetchTourPlaybookOverlays,
  resetTourPlaybookOverlay,
  saveTourPlaybookOverlays,
} from "../../components/funtionsBD/tourGuideService";
import { startPingfloTour } from "../../tour/tourEvents";
import {
  listPlaybookCatalog,
  mergePlaybook,
  setTourPlaybookOverlays,
} from "../../tour/playbooks";
import { TOUR_ID_VALUES } from "../../tour/tourIds";
import "./TourGuideEditor.css";

function cloneBook(book) {
  return JSON.parse(JSON.stringify(book));
}

function blankStep(index) {
  return {
    id: `step-${index + 1}`,
    tourId: "nav-home",
    title: "",
    body: "",
    titleKey: "",
    bodyKey: "",
    route: "/home",
    navTourId: "",
    fallbackTourId: "",
    requireClick: false,
    skipIfMissing: false,
  };
}

export function TourGuideEditor() {
  const { t } = useTranslation();
  const [overlays, setOverlays] = useState({});
  const [playbookId, setPlaybookId] = useState("");
  const [draft, setDraft] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);

  const catalog = useMemo(() => listPlaybookCatalog(), []);

  const applyOverlays = useCallback((map) => {
    const next = map || {};
    setOverlays(next);
    setTourPlaybookOverlays(next);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchTourPlaybookOverlays()
      .then((map) => {
        if (cancelled) return;
        applyOverlays(map);
        const first = listPlaybookCatalog()[0];
        if (first) {
          setPlaybookId(first.id);
          setDraft(cloneBook(mergePlaybook(first, map[first.id])));
          setStepIndex(0);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [applyOverlays]);

  const selectPlaybook = (id) => {
    const base = catalog.find((book) => book.id === id);
    if (!base) return;
    setPlaybookId(id);
    setDraft(cloneBook(mergePlaybook(base, overlays[id])));
    setStepIndex(0);
    setStatus("");
  };

  const step = draft?.steps?.[stepIndex] || null;

  const patchStep = (patch) => {
    setDraft((current) => {
      if (!current) return current;
      const steps = current.steps.map((item, index) =>
        index === stepIndex ? { ...item, ...patch } : item
      );
      return { ...current, steps };
    });
  };

  const addStep = () => {
    setDraft((current) => {
      if (!current) return current;
      const steps = [...current.steps, blankStep(current.steps.length)];
      return { ...current, steps };
    });
    setStepIndex(draft ? draft.steps.length : 0);
  };

  const removeStep = () => {
    if (!draft || draft.steps.length < 2) return;
    const next = draft.steps.filter((_, index) => index !== stepIndex);
    setDraft({ ...draft, steps: next });
    setStepIndex(Math.max(0, stepIndex - 1));
  };

  const moveStep = (delta) => {
    if (!draft) return;
    const nextIndex = stepIndex + delta;
    if (nextIndex < 0 || nextIndex >= draft.steps.length) return;
    const steps = [...draft.steps];
    const [moved] = steps.splice(stepIndex, 1);
    steps.splice(nextIndex, 0, moved);
    setDraft({ ...draft, steps });
    setStepIndex(nextIndex);
  };

  const saveDraft = async () => {
    if (!draft) return;
    setBusy(true);
    setStatus("");
    try {
      const next = await saveTourPlaybookOverlays({
        ...overlays,
        [draft.id]: {
          id: draft.id,
          hostAgentId: draft.hostAgentId || "pingflo",
          steps: draft.steps,
        },
      });
      applyOverlays(next);
      setStatus(t("tour_guide_saved", "Walk saved"));
    } catch (err) {
      setStatus(err.message || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const restoreDefault = async () => {
    if (!playbookId) return;
    setBusy(true);
    try {
      const next = await resetTourPlaybookOverlay(playbookId);
      applyOverlays(next);
      const base = catalog.find((book) => book.id === playbookId);
      setDraft(cloneBook(mergePlaybook(base, next[playbookId])));
      setStepIndex(0);
      setStatus(t("tour_guide_reset", "Restore default"));
    } catch (err) {
      setStatus(err.message || "Could not restore");
    } finally {
      setBusy(false);
    }
  };

  const testStep = () => {
    if (!draft) return;
    setTourPlaybookOverlays({
      ...overlays,
      [draft.id]: draft,
    });
    startPingfloTour({
      playbookId: draft.id,
      role: draft.role,
      startIndex: stepIndex,
      mode: "show",
    });
  };

  return (
    <AdminLayout>
      <div className="tour-guide">
        <header className="tour-guide__head">
          <p className="tour-guide__kicker">{t("tour_guide_catalog", "Catalog")}</p>
          <h1>{t("tour_guide_title", "Guide tours")}</h1>
          <p>{t("tour_guide_subtitle", "Edit any Pingflo walk, then try a step on the real screens.")}</p>
        </header>

        <div className="tour-guide__board">
          <aside className="tour-guide__rail" aria-label={t("tour_guide_catalog", "Catalog")}>
            {catalog.map((book) => (
              <button
                key={book.id}
                type="button"
                className={`tour-guide__book${book.id === playbookId ? " is-active" : ""}`}
                onClick={() => selectPlaybook(book.id)}
              >
                <span>{book.id}</span>
                <em>{book.role}</em>
              </button>
            ))}
          </aside>

          <section className="tour-guide__sheet">
            <ol className="tour-guide__steps">
              {(draft?.steps || []).map((item, index) => (
                <li key={`${item.id}-${index}`}>
                  <button
                    type="button"
                    className={`tour-guide__beat${index === stepIndex ? " is-active" : ""}`}
                    onClick={() => setStepIndex(index)}
                  >
                    <span>{String(index + 1).padStart(2, "0")}</span>
                    <strong>{item.title || item.titleKey || item.id}</strong>
                    <small>{item.tourId}</small>
                  </button>
                </li>
              ))}
            </ol>
            <div className="tour-guide__toolbar">
              <button type="button" onClick={addStep}>
                {t("tour_guide_add_step", "Add step")}
              </button>
              <button type="button" onClick={() => moveStep(-1)} disabled={stepIndex === 0}>
                Up
              </button>
              <button
                type="button"
                onClick={() => moveStep(1)}
                disabled={!draft || stepIndex >= draft.steps.length - 1}
              >
                Down
              </button>
              <button type="button" onClick={removeStep} disabled={!draft || draft.steps.length < 2}>
                Remove
              </button>
            </div>
          </section>

          {step ? (
            <form
              className="tour-guide__form"
              onSubmit={(event) => {
                event.preventDefault();
                saveDraft();
              }}
            >
              <label>
                Id
                <input value={step.id || ""} onChange={(e) => patchStep({ id: e.target.value })} />
              </label>
              <label>
                Highlight
                <select
                  value={step.tourId || ""}
                  onChange={(e) => patchStep({ tourId: e.target.value })}
                >
                  {TOUR_ID_VALUES.map((id) => (
                    <option key={id} value={id}>
                      {id}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Fallback
                <select
                  value={step.fallbackTourId || ""}
                  onChange={(e) => patchStep({ fallbackTourId: e.target.value })}
                >
                  <option value="">—</option>
                  {TOUR_ID_VALUES.map((id) => (
                    <option key={id} value={id}>
                      {id}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Nav
                <select
                  value={step.navTourId || ""}
                  onChange={(e) => patchStep({ navTourId: e.target.value })}
                >
                  <option value="">—</option>
                  {TOUR_ID_VALUES.map((id) => (
                    <option key={id} value={id}>
                      {id}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Route
                <input
                  value={step.route || ""}
                  onChange={(e) => patchStep({ route: e.target.value })}
                />
              </label>
              <label>
                Title key
                <input
                  value={step.titleKey || ""}
                  onChange={(e) => patchStep({ titleKey: e.target.value })}
                />
              </label>
              <label>
                Body key
                <input
                  value={step.bodyKey || ""}
                  onChange={(e) => patchStep({ bodyKey: e.target.value })}
                />
              </label>
              <label>
                Title
                <input
                  value={step.title || ""}
                  onChange={(e) => patchStep({ title: e.target.value })}
                />
              </label>
              <label>
                Spoken line
                <textarea
                  rows={3}
                  value={step.body || ""}
                  onChange={(e) => patchStep({ body: e.target.value })}
                />
              </label>
              <label className="tour-guide__check">
                <input
                  type="checkbox"
                  checked={Boolean(step.requireClick)}
                  onChange={(e) => patchStep({ requireClick: e.target.checked })}
                />
                Wait for click
              </label>
              <label className="tour-guide__check">
                <input
                  type="checkbox"
                  checked={Boolean(step.skipIfMissing)}
                  onChange={(e) => patchStep({ skipIfMissing: e.target.checked })}
                />
                Skip if missing
              </label>
              <div className="tour-guide__actions">
                <button type="button" onClick={testStep}>
                  {t("tour_guide_test", "Try this step")}
                </button>
                <button type="button" onClick={restoreDefault} disabled={busy}>
                  {t("tour_guide_reset", "Restore default")}
                </button>
                <button type="submit" className="is-primary" disabled={busy}>
                  {t("tour_guide_save", "Save walk")}
                </button>
              </div>
              {status ? <p className="tour-guide__status">{status}</p> : null}
            </form>
          ) : null}
        </div>
      </div>
    </AdminLayout>
  );
}
